import React, { useState, useMemo } from "react";
import ApplicationRow from "./ApplicationRow";

interface Application {
  id: number;
  candidate_id: number;
  candidate_first_name?: string;
  candidate_last_name?: string;
  first_name?: string;
  last_name?: string;
  candidate_email?: string;
  email?: string;
  main_skill?: string;
  status: string;
  ai_match_score?: number;
}

type SortKey = "name" | "skill" | "match" | "status";
type SortDir = "asc" | "desc";

interface ApplicationListViewProps {
  applications: Application[];
  onEmailClick: (candidateId: number) => void;
  onGenerateCVClick: (application: Application) => void;
}

const HEADER_STYLE: React.CSSProperties = {
  cursor: "pointer",
  userSelect: "none",
  fontSize: "12px",
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const ApplicationListView: React.FC<ApplicationListViewProps> = ({
  applications,
  onEmailClick,
  onGenerateCVClick,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const arrow = (key: SortKey) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " \u2191" : " \u2193";
  };

  const sorted = useMemo(() => {
    const list = [...applications];
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case "name": {
          const nameA =
            `${a.candidate_first_name || a.first_name || ""} ${a.candidate_last_name || a.last_name || ""}`
              .trim()
              .toLowerCase();
          const nameB =
            `${b.candidate_first_name || b.first_name || ""} ${b.candidate_last_name || b.last_name || ""}`
              .trim()
              .toLowerCase();
          return nameA.localeCompare(nameB) * dir;
        }
        case "skill": {
          const sa = (a.main_skill || "").toLowerCase();
          const sb = (b.main_skill || "").toLowerCase();
          return sa.localeCompare(sb) * dir;
        }
        case "match":
          return ((a.ai_match_score ?? -1) - (b.ai_match_score ?? -1)) * dir;
        case "status":
          return a.status.localeCompare(b.status) * dir;
        default:
          return 0;
      }
    });
    return list;
  }, [applications, sortKey, sortDir]);

  return (
    <div className="applications-list row-view">
      {/* Column Headers */}
      <div className="application-list-header" role="row">
        <div
          role="columnheader"
          tabIndex={0}
          style={{ ...HEADER_STYLE, flex: 2 }}
          onClick={() => toggleSort("name")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleSort("name");
            }
          }}
        >
          Name{arrow("name")}
        </div>
        <div
          role="columnheader"
          tabIndex={0}
          style={{ ...HEADER_STYLE, flex: 1.5 }}
          onClick={() => toggleSort("skill")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleSort("skill");
            }
          }}
        >
          Skill{arrow("skill")}
        </div>
        <div
          role="columnheader"
          tabIndex={0}
          style={{
            ...HEADER_STYLE,
            width: 48,
            textAlign: "center",
            flexShrink: 0,
          }}
          onClick={() => toggleSort("match")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleSort("match");
            }
          }}
        >
          Match{arrow("match")}
        </div>
        <div
          role="columnheader"
          tabIndex={0}
          style={{ ...HEADER_STYLE, width: 130, flexShrink: 0 }}
          onClick={() => toggleSort("status")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleSort("status");
            }
          }}
        >
          Status{arrow("status")}
        </div>
        <div style={{ width: 110, flexShrink: 0 }} />
        <div style={{ width: 36, flexShrink: 0 }} />
      </div>

      {sorted.map((app) => (
        <ApplicationRow
          key={app.id}
          application={app}
          onEmailClick={onEmailClick}
          onGenerateCVClick={onGenerateCVClick}
        />
      ))}
    </div>
  );
};

export default ApplicationListView;
